import { useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { corePayeeBankAccountsClient } from 'app/api/apiClient/generated/corePayeeBankAccountsClient';
import { publicPayeeBankAccountsClient } from 'app/api/apiClient/generated/publicPayeeBankAccountsClient';
import {
  Alpha2Code,
  BankTypeRequirementsDTO,
  ListBankTypeRequirementsResponse,
  UpdateBankAccountRequest,
  UpdateBankAccountResponse,
  UserResponse,
} from 'app/api/apiClient/generated/types';
import { invalidateAuthUser } from 'app/api/hooks/core/v1/auth/user';
import { CoreApiError } from 'app/api/hooks/core/v1/errors';
import { queries } from 'app/constants/queries';
import { useMutationEnhanced } from 'app/hooks/useMutationEnhanced';
import { useQueryEnhanced } from 'app/hooks/useQueryEnhanced';
import { useAlertsDispatch } from 'app/state/AlertsContext/hooks';
import { indexById } from 'app/utils/lib/array';
import { handleErrors } from 'app/utils/lib/context';

// Local Variables
export const expectedBankAccountErrorKeys: string[] = [
  'address.addr1',
  'address.addr2',
  'address.city',
  'address.postalCode',
  'address.state',
  'bankAccountNumber',
  'confirmBankAccountNumber',
  'factorCompanyId',
  'intermediaryRoutingNumber',
  'routingNumber',
];

type CountryTypeRequirements = NonNullable<BankTypeRequirementsDTO['countryRequirements']>;

/**
 * Transforms the keys of an object based on a mapping function while preserving the values.
 * @param obj - The source object whose keys will be transformed
 * @param fn - A function that takes the original key and returns a new key
 * @returns A new object with transformed keys and original values
 */
// eslint-disable-next-line @stylistic/max-len
const mapCountryRequirementKeys = (obj: CountryTypeRequirements, fn: (key: keyof CountryTypeRequirements) => string) => {
  const newObj: Record<string, BankTypeRequirementsDTO | undefined> = {};

  Object.entries(obj).forEach(([key, value]) => {
    newObj[fn(key as keyof CountryTypeRequirements)] = value;
  });

  return newObj as CountryTypeRequirements;
};

export const useGetBankAccountTypes = (options?: { enabled?: boolean }) =>
  useQueryEnhanced(
    [queries.payeeBankAccountTypes],
    publicPayeeBankAccountsClient.listBankTypeRequirements,
    {
      enabled: options?.enabled,
      select: (data: ListBankTypeRequirementsResponse) => {
        return {
          ...data,
          // Note: This is necessary because JsonSerializerSettingFactory.default
          // converts keys to camelCase, which doesn't work when enum keys are used and we need to access them by key.
          bankAccountTypes: data.bankAccountTypes.map(type => {
            const countryRequirements = type.countryRequirements
              ? mapCountryRequirementKeys(type.countryRequirements, key =>
                Alpha2Code[key.toUpperCase() as keyof typeof Alpha2Code].toString())
              : type.countryRequirements;

            return {
              ...type,
              countryRequirements,
            };
          }),
        };
      },
    },
  );

export interface CreateBankAccountError {
  /* eslint-disable @typescript-eslint/naming-convention */
  'address.addr1'?: string[];
  'address.addr2'?: string[];
  'address.city'?: string[];
  'address.postalCode'?: string[];
  'address.state'?: string[];
  /* eslint-enable @typescript-eslint/naming-convention */
  bankAccountNumber?: string[];
  bankAccountTypeId?: string[];
  confirmBankAccountNumber?: string[];
  factorCompanyId?: string[];
  intermediaryRoutingNumber?: string[];
  name?: string[];
  routingNumber?: string[];
}

const updateBankAccount = async (payload: UpdateBankAccountRequest) => {
  try {
    return await corePayeeBankAccountsClient.updateBankAccount(payload);
  } catch (error) {
    if ((error as CoreApiError<CreateBankAccountError>).status === 403) {
      // eslint-disable-next-line @stylistic/max-len
      throw new Error('You are not authorized to perform this action. Please contact support if you believe this is an error.');
    }
    throw error;
  }
};

export const useCreateBankAccount = () => {
  const queryClient = useQueryClient();
  const alertsDispatch = useAlertsDispatch();

  return useMutationEnhanced<
    AxiosResponse<UpdateBankAccountResponse>,
    CoreApiError<CreateBankAccountError>,
    UpdateBankAccountRequest
  >(
    updateBankAccount,
    {
      onError: error => {
        handleErrors({
          dispatch: alertsDispatch,
          errorObject: error as unknown as Error,
          expectedKeys: expectedBankAccountErrorKeys,
        });
      },
      onSuccess: () => {
        invalidateAuthUser(queryClient);
      },
    },
  );
};

export const useLookupAba = () =>
  useMutationEnhanced(publicPayeeBankAccountsClient.lookupAba, {
    onError: () => null, // we display the error inline, we don't want to show the toast
  });

export const useGetBankAccountStatuses = () => {
  const queryClient = useQueryClient();

  const mutationFn = async (ids: string[]) => {
    const results = await Promise.all(ids.map(corePayeeBankAccountsClient.getBankAccountStatus));

    return {
      data: results.map(res => res.data),
    };
  };

  return useMutationEnhanced(mutationFn, {
    onSuccess: data => {
      const userData = queryClient.getQueryData<UserResponse>([queries.authUser]);
      const bankAccounts = indexById(userData?.user.company?.bankAccounts ?? []);

      let hasBankStatusChange = false;

      for (const bankAccount of data) {
        if (!hasBankStatusChange) {
          const existingBankAccount = bankAccounts[bankAccount.id];
          if (existingBankAccount && existingBankAccount.status.id !== bankAccount.status.id) {
            hasBankStatusChange = true;
          }
        } else {
          break;
        }
      }

      if (hasBankStatusChange) {
        invalidateAuthUser(queryClient);
      }
    },
  });
};
